// Where the AI Employee pages send the owner while setup is unfinished: the dashboard gate
// (index.tsx), the sidebar's "Set up" state and the Overview's "Finish setting up" card. Pure, so
// `node --test` covers it (setup-gate.test.ts).
import { isAiReplyOn, type ReplyModeSettings } from './components/reply-modes.ts';

export type SetupStatusValue = 'in_progress' | 'completed' | 'skipped';
const SETUP_STATUSES: readonly SetupStatusValue[] = ['in_progress', 'completed', 'skipped'];

/** The parts of GET /api/ai-employee/settings `ai` the gate reads. */
export interface GateAiSettings extends ReplyModeSettings {
  enabled?: boolean;
  customInstructions?: string;
  topicRules?: unknown[];
  objectives?: unknown[];
  /** `preexisting`: the server saw the AI already set up when the wizard's first save made this record. */
  setup?: { status?: string; step?: string; preexisting?: boolean } | null;
}

/** GET /api/ai-employee/setup/:b (the knowledge summary, not the wizard's setup/:b/state). */
export interface KnowledgeSummary {
  sources?: {
    website?: { count?: number };
    store?: { status?: string };
    productLists?: { count?: number };
    documents?: { count?: number };
    faqs?: { count?: number };
    notes?: { count?: number };
  };
}

/** `ai.setup.status`, or null for a business set up before the v2 wizard (or never started). */
export function setupStatusOf(ai: GateAiSettings | null | undefined): SetupStatusValue | null {
  const status = ai?.setup?.status;
  return SETUP_STATUSES.includes(status as SetupStatusValue) ? (status as SetupStatusValue) : null;
}

export const isSetupDone = (status: SetupStatusValue | null) => status === 'completed' || status === 'skipped';

/** The AI replies in at least one period (the setup controller's `isLive`). */
export const isAiLive = (ai: GateAiSettings | null | undefined) =>
  Boolean(ai && (ai.enabled === true || isAiReplyOn(ai)));

/** Rules, goals or instructions only exist once someone has set the AI up. */
export function hasSavedSettings(ai: GateAiSettings | null | undefined): boolean {
  return Boolean(ai?.customInstructions?.trim() || ai?.topicRules?.length || ai?.objectives?.length);
}

/** Instructions, or topics and goals the wizard didn't create: someone set the AI up outside it. */
export function hasOwnSettings(ai: GateAiSettings | null | undefined): boolean {
  const own = (items?: unknown[]) =>
    Array.isArray(items) && items.some((item) => (item as { origin?: unknown } | null)?.origin !== 'setup');
  return Boolean(ai?.customInstructions?.trim() || own(ai?.topicRules) || own(ai?.objectives));
}

/**
 * A business set up before the v2 wizard, which the wizard must not pull back in. With no setup
 * record: saved settings (the gate's inference) or this browser's completed flag. With a record
 * still in progress (it opened the wizard later): the server's `preexisting` mark, or settings
 * the wizard didn't make. A new business mid-wizard has only what setup created.
 */
export function wasSetUpBeforeV2(ai: GateAiSettings | null | undefined, markedCompleted = false): boolean {
  const status = setupStatusOf(ai);
  if (status === null) return markedCompleted || hasSavedSettings(ai);
  if (status === 'in_progress') return ai?.setup?.preexisting === true || hasOwnSettings(ai);
  return false;
}

export function hasSavedKnowledge(summary?: KnowledgeSummary | null, overview?: string | null): boolean {
  const sources = summary?.sources;
  return Boolean(
    overview?.trim() ||
      sources?.website?.count ||
      ['connected', 'reading'].includes(sources?.store?.status ?? '') ||
      sources?.productLists?.count ||
      sources?.documents?.count ||
      sources?.faqs?.count ||
      sources?.notes?.count,
  );
}

export interface GateInput {
  /** localStorage flag: this browser saw setup completed or skipped. */
  markedCompleted: boolean;
  /** sessionStorage flag: the owner chose "Exit setup" in this tab. */
  exitedThisTab: boolean;
  /** Settings `ai`: undefined while loading, null when the request failed. */
  ai: GateAiSettings | null | undefined;
  /** The knowledge check for a business without a setup record: undefined until it answers, null if it failed. */
  savedKnowledge?: boolean | null;
}

export interface GateDecision {
  view: 'loading' | 'dashboard' | 'setup';
  /** Ask the knowledge endpoints (the inference for businesses set up before the v2 wizard). */
  checkKnowledge: boolean;
  /** Write the localStorage flag. Only a completed or skipped setup does, never an inference. */
  remember: boolean;
}

export function decideSetupGate({ markedCompleted, exitedThisTab, ai, savedKnowledge }: GateInput): GateDecision {
  const status = setupStatusOf(ai);
  const remember = !markedCompleted && isSetupDone(status);
  const decide = (view: GateDecision['view'], checkKnowledge = false): GateDecision => ({ view, checkKnowledge, remember });
  // The fast paths open at once. Exit keeps setup in progress, so without its flag this tab would
  // bounce straight back to /ai/start.
  if (markedCompleted || exitedThisTab) return decide('dashboard');
  if (ai === undefined) return decide('loading');
  // Never redirect on an error: a failed read says nothing about setup, and the pages show their own.
  if (ai === null) return decide('dashboard');
  if (isSetupDone(status)) return decide('dashboard');
  // A business already answering customers, or set up before v2 and then opening the wizard, is
  // not pulled out of its dashboard; the AI sidebar's Setup Wizard link still leads back.
  if (status === 'in_progress') return decide(isAiLive(ai) || wasSetUpBeforeV2(ai) ? 'dashboard' : 'setup');
  // No setup record: infer from what was saved, as before v2. Nothing here sets the flag, so a
  // business that later starts the wizard is still sent back to it.
  if (isAiLive(ai) || hasSavedSettings(ai)) return decide('dashboard');
  if (savedKnowledge === undefined) return decide('loading', true);
  // Only a check that answered "nothing saved" redirects; a failed one opens the dashboard.
  return decide(savedKnowledge === false ? 'setup' : 'dashboard', true);
}

/**
 * The sidebar's "Takes 60 sec · Set up" state: setup neither completed nor skipped, the AI is off,
 * and the business wasn't set up before v2 (a paused one keeps its usual status and escalations).
 * `markedCompleted`: this browser's completed flag.
 */
export function isSetupInvited(ai: GateAiSettings | null | undefined, markedCompleted = false): boolean {
  if (!ai) return false;
  return !isSetupDone(setupStatusOf(ai)) && !isAiLive(ai) && !wasSetUpBeforeV2(ai, markedCompleted);
}

/**
 * The Overview's "Finish setting up" card: setup not completed and the AI off. A business already
 * answering customers, or set up before v2, is left alone (its card would say "then it goes live").
 */
export function showFinishSetup(ai: GateAiSettings | null | undefined, markedCompleted = false): boolean {
  if (!ai || isAiLive(ai)) return false;
  if (setupStatusOf(ai) === 'completed') return false;
  return !wasSetUpBeforeV2(ai, markedCompleted);
}

const CALENDAR_PARAMS = ['calendar', 'calendarConnectionToken', 'calendarBusiness', 'calendarError'];

/**
 * The query to carry to /ai/start: a Google Calendar sign-in's return params (setup finishes the
 * connection), and nothing else.
 */
export function calendarReturnSearch(search: string): string {
  const query = new URLSearchParams(search);
  if (!query.has('calendar')) return '';
  const forwarded = new URLSearchParams();
  for (const key of CALENDAR_PARAMS) {
    const value = query.get(key);
    if (value !== null) forwarded.set(key, value);
  }
  return `?${forwarded}`;
}
